import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './UpdatesSidebar.css';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { FaTelegramPlane, FaWhatsapp } from 'react-icons/fa';

const UpdatesSidebar = ({ isOpen, toggleSidebar }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUpdates = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get updates from Firestore
        const updatesQuery = query(collection(db, "updates"), orderBy("date", "desc"));
        const querySnapshot = await getDocs(updatesQuery);
        const updatesData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          // Convert Firestore Timestamp to JavaScript Date
          const date = data.date && data.date.toDate ? data.date.toDate() : new Date();
          return {
            id: doc.id,
            ...data,
            date: date
          };
        });
        setUpdates(updatesData);
      } catch (error) {
        console.error("Error fetching updates:", error);
        setError("Failed to load updates. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    // Only fetch when the sidebar is open to save resources
    if (isOpen) {
      fetchUpdates();
    }
  }, [isOpen]);

  const formatDate = (date) => {
    if (!date) return '';
    
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date instanceof Date 
      ? date.toLocaleDateString(undefined, options)
      : new Date(date).toLocaleDateString(undefined, options);
  };

  // Truncate content for preview
  const truncateContent = (content, maxLength = 120) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className={`updates-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h2>Latest Updates</h2>
        <div className="channel-icons">
          <a
            href="https://t.me/examguidtutorialservises"
            target="_blank"
            rel="noopener noreferrer"
            className="channel-icon telegram-icon"
            aria-label="Join our Telegram channel"
          >
            <FaTelegramPlane />
            <span className="channel-tooltip">Join this Telegram channel for quick updates!</span>
          </a>
          <a
            href="https://whatsapp.com/channel/0029Vb4uBhZEwEjye8MGEQ47"
            target="_blank"
            rel="noopener noreferrer"
            className="channel-icon whatsapp-icon"
            aria-label="Join our WhatsApp channel"
          >
            <FaWhatsapp />
            <span className="channel-tooltip">Join this WhatsApp channel for quick updates!</span>
          </a>
        </div>
        <button className="close-btn" onClick={toggleSidebar}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      
      <div className="updates-content">
        {loading ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading updates...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : updates.length === 0 ? (
          <div className="no-updates">
            <p>No updates available at this time.</p>
          </div>
        ) : (
          <div className="updates-list">
            {updates.map(update => (
              <div key={update.id} className="update-item">
                <div className="update-header">
                  <h3>{update.title}</h3>
                  <span className="update-date">{formatDate(update.date)}</span>
                </div>
                <div className="update-preview">
                  <p>{truncateContent(update.content.split('\n')[0])}</p>
                  {update.imageUrl && (
                    <div className="update-thumbnail">
                      <img src={update.imageUrl} alt={update.title} />
                    </div>
                  )}
                  <Link to={`/blog/${update.id}`} className="read-more-link">
                    Read More <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdatesSidebar; 